/**
 * Safe formula parser for NexoProject formula columns.
 * Supports: column references {col_id}, arithmetic (+,-,*,/),
 * string concatenation (&), IF(cond, true, false),
 * comparisons (=, !=, >, <, >=, <=), AND, OR, NOT.
 * Does NOT use eval().
 */

export function computeFormula(formulaStr, columnValues, allColumns) {
  if (!formulaStr || typeof formulaStr !== 'string') return '';
  try {
    const substituted = substituteRefs(formulaStr.trim(), columnValues, allColumns);
    const result = parseExpr(tokenize(substituted));
    if (result === null || result === undefined) return '';
    if (typeof result === 'number' && isFinite(result)) {
      return Number.isInteger(result) ? result : Math.round(result * 100) / 100;
    }
    return result;
  } catch (e) {
    return '#ERRO';
  }
}

function substituteRefs(str, columnValues, allColumns) {
  return str.replace(/\{([^}]+)\}/g, (_, colId) => {
    const val = columnValues?.[colId];
    if (val === null || val === undefined) return '0';
    if (typeof val === 'string') return `"${val.replace(/"/g, '\\"')}"`;
    if (typeof val === 'boolean') return val ? '1' : '0';
    return String(val);
  });
}

// ── Tokenizer ──────────────────────────────────────────────────────────────────

function tokenize(str) {
  const tokens = [];
  let i = 0;
  while (i < str.length) {
    const ch = str[i];
    if (/\s/.test(ch)) { i++; continue; }

    // String literals
    if (ch === '"') {
      let j = i + 1;
      while (j < str.length && str[j] !== '"') {
        if (str[j] === '\\') j++;
        j++;
      }
      tokens.push({ type: 'string', value: str.slice(i + 1, j).replace(/\\"/g, '"') });
      i = j + 1;
      continue;
    }

    // Numbers
    if (/[0-9.]/.test(ch)) {
      let j = i;
      while (j < str.length && /[0-9.]/.test(str[j])) j++;
      tokens.push({ type: 'number', value: parseFloat(str.slice(i, j)) });
      i = j;
      continue;
    }

    // Two-char operators
    const two = str.slice(i, i + 2);
    if (['>=', '<=', '!=', '<>'].includes(two)) {
      tokens.push({ type: 'op', value: two === '<>' ? '!=' : two });
      i += 2;
      continue;
    }

    // Single-char operators
    if ('+-*/&=><(),'.includes(ch)) {
      tokens.push({ type: ch === ',' ? 'comma' : 'op', value: ch });
      i++;
      continue;
    }

    // Identifiers / keywords
    if (/[a-zA-Z_]/.test(ch)) {
      let j = i;
      while (j < str.length && /[a-zA-Z0-9_]/.test(str[j])) j++;
      const word = str.slice(i, j).toUpperCase();
      if (['IF', 'AND', 'OR', 'NOT', 'TRUE', 'FALSE'].includes(word)) {
        tokens.push({ type: 'keyword', value: word });
      } else {
        tokens.push({ type: 'ident', value: str.slice(i, j) });
      }
      i = j;
      continue;
    }

    i++; // skip unknown
  }
  tokens.push({ type: 'eof' });
  return tokens;
}

// ── Recursive-descent parser ───────────────────────────────────────────────────

function parseExpr(tokens) {
  let pos = 0;

  function peek() { return tokens[pos]; }
  function consume() { return tokens[pos++]; }

  function matchOp(op) {
    if (peek().type === 'op' && peek().value === op) { consume(); return true; }
    return false;
  }

  function parseOr() {
    let left = parseAnd();
    while (peek().type === 'keyword' && peek().value === 'OR') {
      consume();
      const right = parseAnd();
      left = (toNum(left) !== 0 || toNum(right) !== 0) ? 1 : 0;
    }
    return left;
  }

  function parseAnd() {
    let left = parseNot();
    while (peek().type === 'keyword' && peek().value === 'AND') {
      consume();
      const right = parseNot();
      left = (toNum(left) !== 0 && toNum(right) !== 0) ? 1 : 0;
    }
    return left;
  }

  function parseNot() {
    if (peek().type === 'keyword' && peek().value === 'NOT') {
      consume();
      return toNum(parseNot()) === 0 ? 1 : 0;
    }
    return parseComparison();
  }

  function parseComparison() {
    let left = parseAddSub();
    const tok = peek();
    if (tok.type === 'op' && ['=', '!=', '>', '<', '>=', '<='].includes(tok.value)) {
      consume();
      const right = parseAddSub();
      switch (tok.value) {
        case '=':  return coerceEq(left, right) ? 1 : 0;
        case '!=': return !coerceEq(left, right) ? 1 : 0;
        case '>':  return toNum(left) > toNum(right) ? 1 : 0;
        case '<':  return toNum(left) < toNum(right) ? 1 : 0;
        case '>=': return toNum(left) >= toNum(right) ? 1 : 0;
        case '<=': return toNum(left) <= toNum(right) ? 1 : 0;
        default: return 0;
      }
    }
    return left;
  }

  function parseAddSub() {
    let left = parseMulDiv();
    while (true) {
      const tok = peek();
      if (tok.type === 'op' && tok.value === '+') { consume(); left = toNum(left) + toNum(parseMulDiv()); }
      else if (tok.type === 'op' && tok.value === '-') { consume(); left = toNum(left) - toNum(parseMulDiv()); }
      else if (tok.type === 'op' && tok.value === '&') { consume(); left = String(left) + String(parseMulDiv()); }
      else break;
    }
    return left;
  }

  function parseMulDiv() {
    let left = parseUnary();
    while (true) {
      const tok = peek();
      if (tok.type === 'op' && tok.value === '*') { consume(); left = toNum(left) * toNum(parseUnary()); }
      else if (tok.type === 'op' && tok.value === '/') {
        consume();
        const r = toNum(parseUnary());
        left = r === 0 ? 0 : toNum(left) / r;
      }
      else break;
    }
    return left;
  }

  function parseUnary() {
    if (peek().type === 'op' && peek().value === '-') { consume(); return -toNum(parseAtom()); }
    if (peek().type === 'op' && peek().value === '+') { consume(); return toNum(parseAtom()); }
    return parseAtom();
  }

  function parseAtom() {
    const tok = peek();

    // Parentheses
    if (tok.type === 'op' && tok.value === '(') {
      consume();
      const val = parseOr();
      consume(); // ')'
      return val;
    }

    // Number literal
    if (tok.type === 'number') { consume(); return tok.value; }

    // String literal
    if (tok.type === 'string') { consume(); return tok.value; }

    // TRUE / FALSE
    if (tok.type === 'keyword' && tok.value === 'TRUE') { consume(); return 1; }
    if (tok.type === 'keyword' && tok.value === 'FALSE') { consume(); return 0; }

    // IF(cond, true_val, false_val)
    if (tok.type === 'keyword' && tok.value === 'IF') {
      consume(); consume(); // IF (
      const cond = parseOr();
      consume(); // ,
      const trueVal = parseOr();
      consume(); // ,
      const falseVal = parseOr();
      consume(); // )
      return toNum(cond) !== 0 ? trueVal : falseVal;
    }

    // Generic identifier (treat as 0)
    if (tok.type === 'ident') { consume(); return 0; }

    return 0;
  }

  return parseOr();
}

function toNum(v) {
  if (typeof v === 'number') return v;
  if (typeof v === 'boolean') return v ? 1 : 0;
  const n = parseFloat(v);
  return isNaN(n) ? 0 : n;
}

function coerceEq(a, b) {
  if (typeof a === 'string' || typeof b === 'string') {
    return String(a).toLowerCase() === String(b).toLowerCase();
  }
  return a === b;
}
